import { Button, Group, Modal } from '@mantine/core';
import { isEmpty } from 'lodash/fp';
import React, { useCallback, useRef, useState } from 'react';

import { AutoFormik } from '@portals/autoformik';
import {
  FieldTypeEnum,
  SupportedCommandFieldType,
  SupportedCommandType,
} from '@portals/types';

import CustomFields from './CustomFields';

const FIELDS: Array<SupportedCommandFieldType> = [
  {
    name: 'active',
    title: 'Active and visible to users',
    type: FieldTypeEnum.Checkbox,
    default: true,
  },
  {
    name: 'friendly_name',
    title: 'Friendly name',
    placeholder: 'The name the user will see',
    required: true,
  },
  {
    name: 'description',
    title: 'Description',
    placeholder: 'Detailed description for the user',
    required: true,
  },
  {
    name: 'name',
    title: 'Name sent to device',
    placeholder: 'The name the device will get when this command is sent',
    required: true,
  },
  {
    name: 'with_file',
    title: 'Requires file',
    type: FieldTypeEnum.Checkbox,
    default: false,
  },
  {
    name: 'file_type',
    title: 'Required file type',
    placeholder: 'firmware',
    hidden: ({ with_file }) => !with_file,
  },
  {
    name: 'timeout_in_seconds',
    title: 'Timeout (in seconds)',
    placeholder: 'Maximum possible run time for command',
    type: FieldTypeEnum.Number,
    inputProps: {
      min: 0,
    },
  },
  {
    name: 'allow_duplicate_commands',
    title: 'Allow sending multiple commands with identical parameters',
    type: FieldTypeEnum.Checkbox,
    default: false,
  },
];

interface SupportedCommandFormProps {
  initialValues?: Partial<SupportedCommandType>;
  isEdit: boolean;
  isLoading: boolean;
  errors?: string;
  closeMe: () => void;
  handleUpdate: (data: Record<string, any>) => void;
}

export function SupportedCommandForm({
  isEdit = false,
  isLoading,
  initialValues = {},
  errors,
  closeMe,
  handleUpdate,
}: SupportedCommandFormProps) {
  const [command, setCommand] =
    useState<Partial<SupportedCommandType>>(initialValues);
  const [isValid, setIsValid] = useState(false);

  const onSubmit = () => {
    handleUpdate(command);
  };

  const formRef = useRef(null);
  const validateFields = useCallback(async () => {
    if (formRef.current) {
      const validations = await formRef.current.validateForm();

      setIsValid(isEmpty(validations));
    }
  }, []);

  return (
    <Modal
      opened
      size="xl"
      onClose={closeMe}
      title={isEdit ? 'Edit supported command' : 'Add supported command'}
      sx={(theme) => ({
        '.modal-body': {
          padding: 0,
          marginBottom: theme.spacing.md,
        },
        '.modal-footer': {
          paddingRight: 0,
          paddingLeft: 0,
        },
      })}
    >
      <AutoFormik
        key="add-command"
        fields={FIELDS}
        innerRef={formRef}
        initialValues={initialValues}
        handleSubmit={onSubmit}
        modal={closeMe}
        serverError={errors}
        onChange={(values) => {
          setCommand((curr) => ({
            ...curr,
            ...values,
          }));
          validateFields();
        }}
        inProgress={isLoading}
        actionWrap={() => null}
      />

      <CustomFields command={command} onChange={setCommand} />

      <Group position="right">
        <Button variant="default" onClick={closeMe}>
          Cancel
        </Button>

        <Button disabled={!isValid} data-testid="create-btn" onClick={onSubmit}>
          {isEdit ? 'Update' : 'Create'}
        </Button>
      </Group>
    </Modal>
  );
}
